import { useEffect, useRef, useState, type FocusEvent, type KeyboardEvent } from 'react'
import { Button } from '@highlight/ui'
import { Check, Pencil, X } from 'lucide-react'
import { twMerge } from 'tailwind-merge'

interface EditableTitleProps {
  title: string
  /**
   * Persist the trimmed title. Reject to keep the editor open (the caller owns
   * the error toast). Omit to render the title read-only.
   */
  onSave?: (title: string) => Promise<void>
  /** Off when the caller supplies its own Rename control. */
  showRenameButton?: boolean
  className?: string
  inputClassName?: string
}

/** A saved title, remembered until `title` itself reports the new value. */
interface PendingTitle {
  forTitle: string
  value: string
}

const TITLE_CLASS = 'text-foreground type-ui-sm font-medium'
const PLACEHOLDER = 'Enter a title'

/**
 * Inline-editable title for view headers. The text is a transparent input laid
 * over a sizing span, so clicking or tabbing into the title starts editing in
 * place; save/cancel buttons appear once the draft differs. Enter and blur
 * save, Escape cancels. The saved value keeps showing until the caller's data
 * catches up, so a slow refetch doesn't flash the old title back.
 */
export function EditableTitle({
  title,
  onSave,
  showRenameButton = true,
  className,
  inputClassName,
}: EditableTitleProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  // `null` while not editing.
  const [draft, setDraft] = useState<string | null>(null)
  const [pending, setPending] = useState<PendingTitle | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const committed = pending?.forTitle === title ? pending.value : title
  const isEditing = draft !== null
  const shown = draft ?? committed
  const hasChanges = draft !== null && draft !== committed

  // Leaving edit mode (save or Escape) must also drop focus, otherwise the
  // next keystroke would silently start a new edit in an invisible input.
  useEffect(() => {
    if (!isEditing && document.activeElement === inputRef.current) {
      inputRef.current?.blur()
    }
  }, [isEditing])

  if (!onSave) {
    return (
      <div className={twMerge('flex min-w-0 flex-1 items-center', className)}>
        <span className={twMerge('line-clamp-1', TITLE_CLASS, inputClassName)}>{title}</span>
      </div>
    )
  }

  const cancel = () => setDraft(null)

  const save = async (): Promise<void> => {
    if (draft === null || isSaving) {
      return
    }
    const next = draft.trim()
    if (!next || next === committed) {
      cancel()
      return
    }
    setIsSaving(true)
    try {
      await onSave(next)
      setPending({ forTitle: title, value: next })
      setDraft(null)
    } catch {
      // Stay in edit mode so the user can retry or cancel; the caller reports the error.
    } finally {
      setIsSaving(false)
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()
      // Blur is the single save path.
      event.currentTarget.blur()
    } else if (event.key === 'Escape') {
      // Without this the sidepanel's own Escape handling fires too.
      event.preventDefault()
      event.stopPropagation()
      cancel()
    }
  }

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    // Focus moving to our own save/cancel buttons is not a commit.
    if (event.relatedTarget instanceof Node && rootRef.current?.contains(event.relatedTarget)) {
      return
    }
    void save()
  }

  return (
    <div ref={rootRef} className={twMerge('relative flex min-w-0 flex-1 items-center gap-1', className)}>
      <div className={twMerge('relative min-h-4 min-w-10', isEditing ? 'flex-1' : 'w-fit')}>
        {/* Sizes the box; the input paints on top of it while editing. Cut by character, so a long
            title runs right up to the rename button. */}
        <span className={twMerge('block truncate', TITLE_CLASS, isEditing && 'opacity-0', inputClassName)}>
          {shown.length === 0 ? PLACEHOLDER : shown}
        </span>
        <input
          ref={inputRef}
          placeholder={PLACEHOLDER}
          aria-label="Title"
          value={shown}
          readOnly={isSaving}
          onFocus={() => setDraft(committed)}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          className={twMerge(
            'absolute inset-0 m-0 bg-transparent p-0 outline-none',
            isEditing ? 'opacity-100' : 'opacity-0',
            TITLE_CLASS,
            inputClassName,
          )}
        />
      </div>
      {showRenameButton && !isEditing && (
        <Button
          variant="ghost"
          shape="square"
          size="xs"
          aria-label="Rename"
          className="shrink-0"
          onClick={() => inputRef.current?.focus()}
        >
          <Pencil />
        </Button>
      )}
      {hasChanges && (
        <div className="flex shrink-0 items-center gap-0.5">
          <Button
            variant="ghost"
            shape="square"
            size="xs"
            aria-label="Save title"
            loading={isSaving}
            disabled={!draft.trim()}
            onClick={() => void save()}
          >
            <Check />
          </Button>
          <Button
            variant="ghost"
            shape="square"
            size="xs"
            aria-label="Cancel title edit"
            disabled={isSaving}
            onClick={cancel}
          >
            <X />
          </Button>
        </div>
      )}
    </div>
  )
}
